import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useWallet } from '@/hooks/useWallet.ts'
import { useAdmin } from '@/hooks/useAdmin.ts'
import { AdminOverview } from './AdminOverview.tsx'
import { AdminUserManagement } from './AdminUserManagement.tsx'
import { AdminTreasury } from './AdminTreasury.tsx'
import { cn } from '@/utils/cn.ts'

type AdminTab = 'overview' | 'users' | 'treasury'

export function AdminPage() {
  const { t } = useTranslation()
  const { isConnected, connect } = useWallet()
  const { isOwner, loading: ownerLoading } = useAdmin()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<AdminTab>('overview')

  useEffect(() => {
    if (ownerLoading) return
    if (isConnected && !isOwner) {
      navigate('/dashboard', { replace: true })
    }
  }, [isConnected, isOwner, ownerLoading, navigate])

  const tabClass =
    '-mb-px cursor-pointer border-none border-b-2 border-transparent bg-transparent px-6 py-4 text-[0.9375rem] font-medium text-fg-secondary transition-[color,border-color] duration-fast hover:text-fg'

  if (ownerLoading) {
    return (
      <div className="max-w-content mx-auto px-4 py-8 md:px-8 md:py-12">
        <div className="border-line flex min-h-[300px] flex-col items-center justify-center gap-4 rounded-xl border border-dashed">
          <div className="text-fg-muted text-center text-[0.9375rem]">
            {t('admin.checkingAccess')}
          </div>
        </div>
      </div>
    )
  }

  if (!isConnected) {
    return (
      <div className="max-w-content mx-auto px-4 py-8 md:px-8 md:py-12">
        <h1 className="text-fg mb-1 text-[1.75rem] font-bold max-md:text-[1.375rem]">
          {t('admin.title')}
        </h1>
        <p className="text-fg-secondary mb-12">{t('admin.loginSubtitle')}</p>
        <div className="border-line bg-surface-raised max-w-[400px] rounded-xl border p-12">
          <p className="text-fg-secondary mb-6">{t('admin.connectHint')}</p>
          <button
            type="button"
            className="bg-gradient-brand text-fg-inverse duration-fast cursor-pointer rounded-md border-none px-6 py-3 text-[0.9375rem] font-semibold transition-opacity hover:opacity-90"
            onClick={connect}
          >
            {t('admin.connectWallet')}
          </button>
        </div>
      </div>
    )
  }

  if (!isOwner) return null

  return (
    <div className="max-w-content mx-auto px-4 py-8 md:px-8 md:py-12">
      <h1 className="text-fg mb-1 text-[1.75rem] font-bold max-md:text-[1.375rem]">
        {t('admin.title')}
      </h1>
      <p className="text-fg-secondary mb-12">{t('admin.subtitle')}</p>

      <nav
        className="border-line mb-8 flex flex-wrap gap-1 border-b max-md:flex-wrap"
        aria-label={t('admin.tabsLabel')}
      >
        <button
          type="button"
          className={cn(
            tabClass,
            activeTab === 'overview' && 'border-b-accent text-accent',
          )}
          onClick={() => setActiveTab('overview')}
        >
          {t('admin.tabOverview')}
        </button>
        <button
          type="button"
          className={cn(
            tabClass,
            activeTab === 'users' && 'border-b-accent text-accent',
          )}
          onClick={() => setActiveTab('users')}
        >
          {t('admin.tabUserManagement')}
        </button>
        <button
          type="button"
          className={cn(
            tabClass,
            activeTab === 'treasury' && 'border-b-accent text-accent',
          )}
          onClick={() => setActiveTab('treasury')}
        >
          {t('admin.tabTreasury')}
        </button>
      </nav>

      <div className="min-h-[200px]">
        {activeTab === 'overview' && <AdminOverview />}
        {activeTab === 'users' && <AdminUserManagement />}
        {activeTab === 'treasury' && <AdminTreasury />}
      </div>
    </div>
  )
}
